/**
 * Firestore reads for the map and dashboard come from the data project
 * (floodcenter-backend-v2) and are read only. The one write, alert signups,
 * goes to the v3 site project.
 *
 *   sensor_locations/{deviceId}   latitude, longitude, sensor_height, yellow_height, red_height (mm)
 *   waterLevel_daily/{id}_{date}  deviceId, date, day, maxMm { 'HH:MM': mm }
 *   waterLevel_latest/{deviceId}  levelMm, at
 *   rainForecast_daily/{id}_{date} deviceId, date, day, slots { 'HH:MM': { mm, issuedAt, final, source } }
 *   alerts/{deviceId}             level, reason, rainFwd6h, rainPast12h, holdDown, since, ruleClearedAt, updatedAt
 *   alerts_rf/{deviceId}          random forest alert-rf-v1: level ('red' | 'none'), probability, gap, holdDown, ruleClearedAt, updatedAt
 *   alert_history/{episodeId}     deviceId, mode, startAt, ruleClearedAt, endAt, peakLevel, levels [{ at, level }]
 *   alert_history_rf/{episodeId}  the same for alert-rf-v1 (mode 'live-rf' | 'replay-rf'), plus peakProbability
 *   canal_forecasts/{deviceId}    issuedAt, modelId, points [{ t, waterLevelMm }]
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import {
  alertDeviceId,
  isAlertEmail,
  isAlertPhone,
  normalizeAlertEmail,
  normalizeAlertPhone,
} from './alertSignup';
import { dataDb, signupDb } from './firebase';
import { dailyDocBounds, toMs } from './waterLevelQuery';

// Rain slots are keyed by their start; one starting this long before the
// range still overlaps it.
const RAIN_SLOT_MS = 3 * 60 * 60 * 1000;
// Episodes are queried by startAt; one that started this long before the
// range can still overlap it. The longest replayed episode is about 3 days.
const MAX_EPISODE_MS = 7 * 24 * 60 * 60 * 1000;
const ALERT_HISTORY_MODES = ['live', 'replay-issued'];

const numberOrNull = (value) => {
  const n = Number(value);
  return value != null && Number.isFinite(n) ? n : null;
};

const toSensor = (snap) => {
  const data = snap.data();
  return {
    id: snap.id,
    name: data.name || snap.id,
    latitude: numberOrNull(data.latitude),
    longitude: numberOrNull(data.longitude),
    sensorHeightMm: numberOrNull(data.sensor_height),
    yellowMm: numberOrNull(data.yellow_height),
    redMm: numberOrNull(data.red_height),
  };
};

export const fetchSensorLocations = async () => {
  const snap = await getDocs(collection(dataDb, 'sensor_locations'));
  return snap.docs.map(toSensor);
};

export const fetchSensorLocation = async (deviceId) => {
  const snap = await getDoc(doc(dataDb, 'sensor_locations', deviceId));
  return snap.exists() ? toSensor(snap) : null;
};

const toLatest = (data) => {
  if (!data) return null;
  const levelMm = numberOrNull(data.levelMm);
  const atMs = data.at && typeof data.at.toMillis === 'function' ? data.at.toMillis() : null;
  return levelMm == null ? null : { levelMm, atMs };
};

/** Calls onChange with { [deviceId]: { levelMm, atMs } } on every update. */
export const subscribeLatestLevels = (onChange, onError) =>
  onSnapshot(
    collection(dataDb, 'waterLevel_latest'),
    (snap) => {
      const byDevice = {};
      snap.forEach((d) => {
        const latest = toLatest(d.data());
        if (latest) byDevice[d.id] = latest;
      });
      onChange(byDevice);
    },
    onError
  );

/** waterLevel_latest, alerts, alerts_rf and canal_forecasts docs for one device. */
export const fetchDeviceStatus = async (deviceId) => {
  const [latestSnap, alertSnap, rfAlertSnap, forecastSnap] = await Promise.all([
    getDoc(doc(dataDb, 'waterLevel_latest', deviceId)),
    getDoc(doc(dataDb, 'alerts', deviceId)),
    getDoc(doc(dataDb, 'alerts_rf', deviceId)),
    getDoc(doc(dataDb, 'canal_forecasts', deviceId)),
  ]);
  return {
    latest: latestSnap.exists() ? toLatest(latestSnap.data()) : null,
    alert: alertSnap.exists() ? alertSnap.data() : null,
    rfAlert: rfAlertSnap.exists() ? rfAlertSnap.data() : null,
    forecast: forecastSnap.exists() ? forecastSnap.data() : null,
  };
};

/** waterLevel_daily docs covering [startMs, endMs] (UTC days). */
export const fetchDailyDocs = async (deviceId, startMs, endMs) => {
  const { fromDayMs, toDayMs } = dailyDocBounds(startMs, endMs);
  const snap = await getDocs(query(
    collection(dataDb, 'waterLevel_daily'),
    where('deviceId', '==', deviceId),
    where('day', '>=', Timestamp.fromMillis(fromDayMs)),
    where('day', '<=', Timestamp.fromMillis(toDayMs)),
    orderBy('day', 'asc')
  ));
  return snap.docs.map((d) => d.data());
};

/** rainForecast_daily docs whose 3-hour slots can overlap [startMs, endMs] (UTC days). */
export const fetchRainForecastDocs = async (deviceId, startMs, endMs) => {
  const { fromDayMs, toDayMs } = dailyDocBounds(startMs - RAIN_SLOT_MS, endMs);
  const snap = await getDocs(query(
    collection(dataDb, 'rainForecast_daily'),
    where('deviceId', '==', deviceId),
    where('day', '>=', Timestamp.fromMillis(fromDayMs)),
    where('day', '<=', Timestamp.fromMillis(toDayMs)),
    orderBy('day', 'asc')
  ));
  return snap.docs.map((d) => d.data());
};

const startAtRange = (startMs, endMs) => [
  where('startAt', '>=', Timestamp.fromMillis(startMs - MAX_EPISODE_MS)),
  where('startAt', '<=', Timestamp.fromMillis(endMs)),
  orderBy('startAt', 'asc'),
];

// alert_history_rf has no deviceId + startAt index, so it is queried by
// startAt alone and the device is filtered here (a few episodes a month).
const alertHistoryQuery = (source, deviceId, startMs, endMs) => (source === 'rf'
  ? query(collection(dataDb, 'alert_history_rf'), ...startAtRange(startMs, endMs))
  : query(
    collection(dataDb, 'alert_history'),
    where('deviceId', '==', deviceId),
    where('mode', 'in', ALERT_HISTORY_MODES),
    ...startAtRange(startMs, endMs)
  ));

/**
 * Alert episodes that can overlap [startMs, endMs], as { id, mode, startMs,
 * endMs (null = open), ruleClearedMs, levels [{ atMs, level }], peakLevel,
 * dataQuality }. source 'rule': the rain rule's alert_history (live and
 * replay-issued). source 'rf': the random forest's alert_history_rf
 * (live-rf and replay-rf).
 */
export const fetchAlertHistory = async (deviceId, startMs, endMs, source = 'rule') => {
  const snap = await getDocs(alertHistoryQuery(source, deviceId, startMs, endMs));
  return snap.docs
    .filter((d) => d.get('deviceId') === deviceId)
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        mode: data.mode,
        startMs: toMs(data.startAt),
        endMs: toMs(data.endAt),
        ruleClearedMs: toMs(data.ruleClearedAt),
        levels: (data.levels || [])
          .map((l) => ({ atMs: toMs(l.at), level: l.level }))
          .filter((l) => l.atMs != null),
        peakLevel: data.peakLevel,
        dataQuality: data.dataQuality,
      };
    })
    .filter((e) => e.startMs != null && (e.endMs == null || e.endMs >= startMs));
};
/**
 * Stores an email or mobile number for Watch / Flooding alerts.
 * deviceId null means every sensor. Written to alert_subscriptions in the v3
 * site project, whose rules allow create only; nobody can read them back.
 */
export const saveAlertSubscription = async ({ email, phone, deviceId }) => {
  const sensorId = alertDeviceId(deviceId);
  if (sensorId === undefined) {
    const error = new Error('Choose a sensor from the list.');
    error.code = 'invalid-sensor';
    throw error;
  }
  const record = { deviceId: sensorId, createdAt: serverTimestamp() };
  if (phone != null && String(phone).trim() !== '') {
    const normalizedPhone = normalizeAlertPhone(phone);
    if (!isAlertPhone(normalizedPhone)) {
      const error = new Error('Enter a valid mobile number.');
      error.code = 'invalid-phone';
      throw error;
    }
    record.phone = normalizedPhone;
  } else {
    const normalizedEmail = normalizeAlertEmail(email);
    if (!isAlertEmail(normalizedEmail)) {
      const error = new Error('Enter a valid email address.');
      error.code = 'invalid-email';
      throw error;
    }
    record.email = normalizedEmail;
  }
  await addDoc(collection(signupDb, 'alert_subscriptions'), record);
};
